import { ComponentProps, createContext, forwardRef, useContext } from "react";
import { styled, TamaguiElement, Text } from "tamagui";

import { Ellipsis, Pin } from "../../../icons";
import { Button as ButtonBase, Card } from "../../ui";

const GroupCardContext = createContext<{ disabled?: boolean }>({});

const useGroupContext = () => {
  const context = useContext(GroupCardContext);
  if (!context) {
    throw new Error(
      "useGroupContext must be used within a GroupCardContext.Provider",
    );
  }
  return context;
};

type BodyProps = ComponentProps<typeof Card> & {
  disabled?: boolean;
};

const Body = forwardRef<TamaguiElement, BodyProps>(
  ({ children, disabled, ...rest }, ref) => {
    return (
      <GroupCardContext.Provider value={{ disabled }}>
        <Card ref={ref} {...rest}>
          {children}
        </Card>
      </GroupCardContext.Provider>
    );
  },
);

const GroupCardLabel = styled(Text, {
  name: "GroupCardLabel",
  fontFamily: "$subtitle",
  fontWeight: "$4",
  fontSize: "$2",
  lineHeight: "$2",
  color: "$gray-600",
});

const GroupCardTitleBase = styled(Text, {
  name: "GroupCardTitle",
  fontFamily: "$subtitle",
  fontWeight: "$6",
  fontSize: "$6",
  lineHeight: "$6",
  variants: {
    disabled: {
      true: {
        color: "$gray-secondary-400",
      },
      false: {
        color: "$gray-primary-900",
      },
    },
  } as const,
  defaultVariants: {
    disabled: false,
  },
});

type GroupCardTitleProps = ComponentProps<typeof GroupCardTitleBase>;

const GroupCardTitle: React.FC<Omit<GroupCardTitleProps, "disabled">> = (
  props,
) => {
  const { disabled } = useGroupContext();
  return <GroupCardTitleBase disabled={disabled} {...props} />;
};

type ButtonProps = ComponentProps<typeof ButtonBase>;

const Button: React.FC<ButtonProps> = ({ children, ...rest }) => {
  return (
    <ButtonBase
      mode={"icon"}
      borderColor={"$gray-200"}
      variant="outlined"
      size="sm"
      rounded="lg"
      {...rest}
    >
      {children}
    </ButtonBase>
  );
};

type CardProps = ComponentProps<typeof Card>;

const StatCard: React.FC<CardProps> = (props) => {
  const { disabled } = useGroupContext();
  return (
    <Card
      gap={"$0.5"}
      justifyContent="center"
      alignItems="center"
      flex={1}
      paddingVertical={"$2"}
      variant={disabled ? "default" : "borderless"}
      rounded="md"
      paddingHorizontal={0}
      backgroundColor={disabled ? "white" : "$gray-50"}
      borderColor={disabled ? "$gray-200" : "transparent"}
      {...props}
    />
  );
};

const StatCardScoreBase = styled(Text, {
  name: "StatCardScore",
  fontFamily: "$subtitle",
  fontWeight: "$6",
  fontSize: "$6",
  lineHeight: "$6",

  variants: {
    disabled: {
      true: {
        color: "$gray-secondary-400",
      },
      false: {
        color: "$gray-primary-900",
      },
    },
  } as const,
  defaultVariants: {
    disabled: false,
  },
});

type StatCardScoreProps = ComponentProps<typeof StatCardScoreBase>;

const StatCardScore: React.FC<Omit<StatCardScoreProps, "disabled">> = (
  props,
) => {
  const { disabled } = useGroupContext();
  return <StatCardScoreBase disabled={disabled} {...props} />;
};

const StatCardScoreContainer = styled(Text, {
  name: "StatCardScoreContainer",
  fontFamily: "$subtitle",
  fontWeight: "$4",
  fontSize: "$6",
  lineHeight: "$6",
  color: "$gray-secondary-400",
});

const StatCardDescription = styled(Text, {
  name: "StatCardDescription",
  textAlign: "center",
  fontFamily: "$body",
  fontWeight: "$4",
  fontSize: "$3",
  lineHeight: "$3",
  color: "$gray-650",
});

export const GroupCard = {
  Body,
  Button,
  EllipsisIcon: Ellipsis,
  PinIcon: Pin,
  Label: GroupCardLabel,
  Title: GroupCardTitle,
  StatCard,
  Score: StatCardScore,
  ScoreContainer: StatCardScoreContainer,
  StatDescription: StatCardDescription,
};
